import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Info, Trash2 } from 'lucide-react';
import {
  AppButton, AppModal, Callout, FieldGroup, FormField, Input, Radio, Switch, Textarea, UserAvatar, cn,
} from '../ui';
import SkillsInput from './SkillsInput';
import { TEAM_KINDS, TEAM_KIND_LABEL, teamKindOf } from './teamKinds';
import {
  createTeamMember, deleteCmsImage, updateTeamMember, uploadCmsImage,
} from '../../utils/cmsAdminApi';

// ─── TeamMemberModal — add or edit one website profile ───────────────────
//
// The limits mirror vc_be/utils/cmsTeamProfile.js. They are repeated rather
// than fetched because they are part of this form's layout — a counter cannot
// be drawn around a number that arrives later — and a drift between the two
// shows up as a field the server quietly shortens.
const MAX = { name: 120, designation: 160, experience: 80, bio: 2000 };
const SKILLS = { max: 20, maxLength: 40 };
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

const EMPTY = {
  kind: 'trainer', name: '', designation: '', bio: '', experience: '',
  skills: [], linkedin: '', photoKey: '', photoUrl: '', isActive: true,
};

const fromMember = (member) => ({
  ...EMPTY,
  ...member,
  kind: teamKindOf(member),
  skills: Array.isArray(member?.skills) ? member.skills : [],
  isActive: member?.isActive !== false,
});

// What each choice means on the website, in the admin's terms.
const KIND_HINT = {
  trainer: 'Teaches your courses.',
  mentor: 'Guides students — projects, interviews, careers.',
  both: 'Teaches, and mentors students too.',
};

/**
 * Props:
 *   member   — null (closed), {} (new) or the profile to edit
 *   onClose  — ()
 *   onSaved  — (message) closes and refreshes the list
 *   notify   — (message, type)
 */
const TeamMemberModal = ({ member, onClose, onSaved, notify }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  // A photo uploaded in this sitting and then replaced is deleted from
  // storage; the one the profile was saved with is left alone until the save
  // succeeds, so cancelling cannot blank a live website card.
  const sessionKeys = useRef(new Set());

  const isEdit = !!member?._id;

  useEffect(() => {
    if (!member) return;
    setForm(fromMember(member));
    setError('');
    sessionKeys.current = new Set();
  }, [member]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setText = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const photo = form.photoUrl || '';
  const nameTrimmed = form.name.trim();

  const discardSessionPhoto = (key) => {
    if (!key || !sessionKeys.current.has(key)) return;
    sessionKeys.current.delete(key);
    deleteCmsImage(key).catch(() => {});
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return setError('Choose a JPG, PNG or WebP image.');
    if (file.size > PHOTO_MAX_BYTES) return setError('The photo must be smaller than 5 MB.');
    setError('');
    setUploading(true);
    try {
      const res = await uploadCmsImage(file, 'team-photo');
      discardSessionPhoto(form.photoKey);
      sessionKeys.current.add(res.key);
      setForm((f) => ({ ...f, photoKey: res.key, photoUrl: res.url }));
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not upload the photo.');
    } finally {
      setUploading(false);
    }
    return undefined;
  };

  const removePhoto = () => {
    discardSessionPhoto(form.photoKey);
    setForm((f) => ({ ...f, photoKey: '', photoUrl: '' }));
  };

  const save = async () => {
    if (!nameTrimmed) {
      setError('A name is required — it is the one thing the card cannot do without.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload = {
        kind: teamKindOf(form),
        name: nameTrimmed,
        designation: form.designation.trim(),
        bio: form.bio.trim(),
        experience: form.experience.trim(),
        skills: form.skills,
        linkedin: form.linkedin.trim(),
        photoKey: form.photoKey || '',
        // An uploaded photo is stored as a key; the URL beside it is a
        // presigned preview that expires, so it is never saved.
        photoUrl: form.photoKey ? '' : form.photoUrl.trim(),
        isActive: form.isActive !== false,
      };
      if (isEdit) {
        await updateTeamMember(member._id, payload);
        onSaved(`${payload.name}'s profile was saved`);
      } else {
        await createTeamMember(payload);
        onSaved(`${payload.name} was added to your website team`);
      }
      sessionKeys.current = new Set();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save this profile.');
    } finally {
      setSaving(false);
    }
  };

  // The card the visitor will see. Worth the few lines: the admin is writing
  // copy for a page they are not looking at, and a designation that overflows
  // or a bio that is three words long is obvious here and invisible in a form.
  const preview = useMemo(() => ({
    kind: TEAM_KIND_LABEL[teamKindOf(form)],
    name: nameTrimmed || 'Their name',
    designation: form.designation.trim(),
    experience: form.experience.trim(),
    skills: form.skills.slice(0, 4),
  }), [form, nameTrimmed]);

  return (
    <AppModal
      show={!!member}
      onHide={onClose}
      title={isEdit ? `Edit ${member?.name || 'profile'}` : 'Add a website profile'}
      size="lg"
      footer={(
        <>
          <AppButton variant="outline" onClick={onClose} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={save} disabled={saving || uploading}>
            {saving ? 'Saving…' : isEdit ? 'Save profile' : 'Add to website'}
          </AppButton>
        </>
      )}
    >
      {member && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          {!isEdit && (
            <Callout tone="info" icon={<Info size={14} aria-hidden="true" />}>
              This creates a website profile only — no login, no invitation email and no
              instructor seat used.
            </Callout>
          )}

          {/* Photo and the live card, side by side. */}
          <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-start">
            <div className="tw-flex tw-flex-1 tw-flex-wrap tw-items-center tw-gap-3">
              <UserAvatar name={preview.name} src={photo || undefined} size="xl" bordered />
              <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  <AppButton
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                  >
                    <ImagePlus size={16} />
                    {uploading ? 'Uploading…' : photo ? 'Change photo' : 'Upload photo'}
                  </AppButton>
                  {photo && !uploading && (
                    <AppButton type="button" size="sm" variant="ghost-destructive" onClick={removePhoto}>
                      <Trash2 size={16} /> Remove
                    </AppButton>
                  )}
                </div>
                <span className="tw-text-xs tw-text-content-subtle">
                  {photo
                    ? 'A square photo looks best — the website crops to a square.'
                    : 'JPG, PNG or WebP, up to 5 MB. Without one, the card shows their initials.'}
                </span>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept={PHOTO_ACCEPT}
                onChange={pickPhoto}
                className="tw-hidden"
                tabIndex={-1}
                aria-hidden="true"
              />
            </div>

            <div
              className={cn(
                'tw-w-full tw-shrink-0 tw-rounded-md tw-border tw-border-solid tw-border-line',
                'tw-bg-surface-alt tw-p-3 sm:tw-w-52',
              )}
            >
              <span className="tw-mb-2 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-subtle">
                On your website
              </span>
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-sm tw-bg-surface tw-p-3 tw-text-center">
                <UserAvatar name={preview.name} src={photo || undefined} size="lg" />
                {/* The website prints this only beside people of the other
                    kind; here it is always shown, so the choice is visible. */}
                <span className="tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-primary">
                  {preview.kind}
                </span>
                <span className="tw-text-sm tw-font-semibold tw-text-content">{preview.name}</span>
                {preview.designation && (
                  <span className="tw-text-xs tw-text-content-subtle">{preview.designation}</span>
                )}
                {preview.experience && (
                  <span className="tw-text-xs tw-text-content-subtle">{preview.experience}</span>
                )}
                {preview.skills.length > 0 && (
                  <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-1">
                    {preview.skills.map((skill) => (
                      <span
                        key={skill}
                        className="tw-rounded-full tw-bg-primary/10 tw-px-1.5 tw-py-0.5 tw-text-xs tw-text-primary"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <FieldGroup
            legend="Show them as"
            hint="Everyone appears in one Trainers & mentors section; this is the label on their card and the group they fall in when visitors filter it."
          >
            <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-3">
              {TEAM_KINDS.map((kind) => (
                <Radio
                  key={kind}
                  variant="card"
                  name="teamKind"
                  value={kind}
                  checked={teamKindOf(form) === kind}
                  onChange={() => set('kind')(kind)}
                  label={TEAM_KIND_LABEL[kind]}
                  description={KIND_HINT[kind]}
                />
              ))}
            </div>
          </FieldGroup>

          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
            <FormField label="Name" required>
              <Input
                size="lg"
                maxLength={MAX.name}
                value={form.name}
                onChange={setText('name')}
                placeholder="e.g. Ananya Verma"
              />
            </FormField>

            <FormField label="Designation" hint="The line under their name.">
              <Input
                size="lg"
                maxLength={MAX.designation}
                value={form.designation}
                onChange={setText('designation')}
                placeholder="e.g. Senior Data Scientist, Visiting Faculty"
              />
            </FormField>

            <FormField label="Experience" hint="Written out — it is shown as typed.">
              <Input
                size="lg"
                maxLength={MAX.experience}
                value={form.experience}
                onChange={setText('experience')}
                placeholder="e.g. 8 years"
              />
            </FormField>

            <FormField label="LinkedIn" hint="Optional. Shown as a link on the card.">
              <Input
                size="lg"
                type="url"
                value={form.linkedin}
                onChange={setText('linkedin')}
                placeholder="linkedin.com/in/ananya-verma"
              />
            </FormField>

            <FormField label="Skills" className="sm:tw-col-span-2">
              <SkillsInput
                value={form.skills}
                onChange={set('skills')}
                max={SKILLS.max}
                maxLength={SKILLS.maxLength}
              />
            </FormField>

            <FormField
              label="Bio"
              className="sm:tw-col-span-2"
              hint={`Leave a blank line between paragraphs. ${form.bio.length} of ${MAX.bio} characters.`}
            >
              <Textarea
                size="lg"
                rows={5}
                maxLength={MAX.bio}
                value={form.bio}
                onChange={setText('bio')}
                placeholder="A short introduction for prospective students — what they teach, where they have worked, what they are known for…"
              />
            </FormField>
          </div>

          <Switch
            checked={form.isActive !== false}
            onChange={set('isActive')}
            label="Show on my website"
            description={form.isActive !== false
              ? 'Appears in the Trainers & mentors section of your About page — and your home page, if you switched it on there — as soon as you save.'
              : 'Kept here, but not shown to visitors. Use this while you are still writing the copy.'}
          />

          {error && <Callout tone="danger" role="alert">{error}</Callout>}
        </div>
      )}
    </AppModal>
  );
};

export default TeamMemberModal;
